import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/shared/state/auth-context';

type Mode = 'login' | 'signup';

export function AuthForm({ mode }: { mode: Mode }) {
  const { signIn, signUp } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === 'login') await signIn(email, password);
      else await signUp({ email, password, username });
      navigate('/instaflix');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="w-full">
      <div className="text-center mb-8">
        <h1 className="font-serif italic text-3xl text-gradient-rose tracking-widest">PikPok</h1>
        <p className="text-white/40 text-xs mt-2 tracking-wider uppercase">
          {mode === 'login' ? 'Sign in to continue' : 'Create your account'}
        </p>
      </div>
      <form data-testid="auth-form" data-mode={mode} onSubmit={submit} className="space-y-3">
        {mode === 'signup' && (
          <input
            data-testid="auth-username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Username"
            className="w-full glass rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/40 outline-none"
          />
        )}
        <input
          data-testid="auth-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          autoComplete="email"
          className="w-full glass rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/40 outline-none"
        />
        <input
          data-testid="auth-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
          className="w-full glass rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/40 outline-none"
        />
        {error && <div data-testid="auth-error" className="text-rose-300 text-xs px-1">{error}</div>}
        <button
          data-testid="auth-submit"
          type="submit"
          disabled={busy}
          className="w-full bg-rose-100 text-velvet font-medium rounded-xl py-3 active:scale-[0.98] transition-transform disabled:opacity-50"
        >
          {busy ? 'Please wait…' : mode === 'login' ? 'Sign in' : 'Create account'}
        </button>
      </form>
      <div className="mt-5 text-center text-white/50 text-xs">
        {mode === 'login' ? (
          <>No account? <Link to="/signup" className="text-rose-200">Create one</Link></>
        ) : (
          <>Already have one? <Link to="/login" className="text-rose-200">Sign in</Link></>
        )}
      </div>
      <div className="mt-3 text-center">
        <button
          type="button"
          data-testid="auth-guest"
          onClick={async () => {
            try {
              await signIn('guest@pikpok.app', 'guest');
              navigate('/instaflix');
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Something went wrong.');
            }
          }}
          className="text-white/40 text-xs"
        >
          Continue as guest →
        </button>
      </div>
    </div>
  );
}
